import { useState, useEffect } from "react";
import "./TicketAdminPanel.css";
import ConventionTicketModel from "../../model/ConventionTicketModel";
import AlertMsg from "../AlertMsg/AlertMsg";
import AlertMsgError from "../AlertMsg/AlertMsgError";

const TicketAdminPanel = () => {
    const [tickets, setTickets] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("date");
    const [filterCountry, setFilterCountry] = useState("all");
    const [visible, setVisible] = useState(false);
    const [visibleError, setVisibleError] = useState(false);
    const [msj, setMsj] = useState("");
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    useEffect(() => {
        loadTickets();
        loadStatistics();
    }, []);

    const loadTickets = () => {
        ConventionTicketModel.getAllTickets((data) => {
            if (data) {
                const ticketArray = Object.values(data);
                setTickets(ticketArray);
            } else {
                setTickets([]);
            }
            setLoading(false);
        });
    };

    const loadStatistics = () => {
        ConventionTicketModel.getStatistics((stats) => {
            setStatistics(stats);
        });
    };

    const handleCancelTicket = async (userId, ticketNumber) => {
        if (window.confirm(`¿Estás seguro de cancelar el ticket ${ticketNumber}?`)) {
            try {
                await ConventionTicketModel.cancelTicket(userId);
                setMsj("Ticket cancelado exitosamente");
                setVisible(true);
                loadTickets();
                loadStatistics();
            } catch (error) {
                setMsj(error.message || "Error al cancelar ticket");
                setVisibleError(true);
            }
        }
    };

    const viewTicketDetails = (ticket) => {
        setSelectedTicket(ticket);
        setShowDetailModal(true);
    };

    const exportToCSV = () => {
        const headers = ["Ticket Number", "Name", "Email", "Country", "Phone", "Purchase Date", "Amount"];
        const rows = filteredTickets.map(ticket => [
            ticket.ticketNumber,
            `${ticket.firstName} ${ticket.lastName}`,
            ticket.email,
            ticket.country,
            ticket.phone || "N/A",
            new Date(ticket.purchaseDate).toLocaleDateString(),
            `$${ticket.ticketPrice} USDT`
        ]);

        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += headers.join(",") + "\n";
        rows.forEach(row => {
            csvContent += row.join(",") + "\n";
        });

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `las_vegas_tickets_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Filtrar y ordenar tickets
    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch = 
            ticket.ticketNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            `${ticket.firstName} ${ticket.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ticket.email.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCountry = filterCountry === "all" || ticket.country === filterCountry;
        
        return matchesSearch && matchesCountry;
    }).sort((a, b) => {
        if (sortBy === "date") {
            return new Date(b.purchaseDate) - new Date(a.purchaseDate);
        } else if (sortBy === "name") {
            return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        } else if (sortBy === "country") {
            return a.country.localeCompare(b.country);
        }
        return 0;
    });

    // Obtener lista de países únicos
    const countries = ["all", ...new Set(tickets.map(t => t.country))];

    if (loading) {
        return (
            <div className="ticket-admin-loading">
                <div className="spinner-large"></div>
                <p>Loading ticket data...</p>
            </div>
        );
    }

    return (
        <div className="ticket-admin-panel">
            <AlertMsg visible={visible} setVisible={setVisible} texto={msj} />
            <AlertMsgError visible={visibleError} setVisible={setVisibleError} texto={msj} />

            <div className="admin-header">
                <div className="header-title">
                    <i className="bi bi-ticket-perforated"></i>
                    <h2>Las Vegas Convention 2026 - Ticket Management</h2>
                </div>
            </div>

            {/* Estadísticas */}
            {statistics && (
                <div className="statistics-grid">
                    <div className="stat-card">
                        <div className="stat-icon">
                            <i className="bi bi-ticket-detailed-fill"></i>
                        </div>
                        <div className="stat-content">
                            <p className="stat-label">Total Tickets Sold</p>
                            <p className="stat-value">{statistics.totalTickets}</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon revenue">
                            <i className="bi bi-cash-stack"></i>
                        </div>
                        <div className="stat-content">
                            <p className="stat-label">Total Revenue</p>
                            <p className="stat-value">${statistics.totalRevenue} USDT</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon countries">
                            <i className="bi bi-globe"></i>
                        </div>
                        <div className="stat-content">
                            <p className="stat-label">Countries</p>
                            <p className="stat-value">{Object.keys(statistics.byCountry || {}).length}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Controles */}
            <div className="admin-controls">
                <div className="search-box">
                    <i className="bi bi-search"></i>
                    <input
                        type="text"
                        placeholder="Search by ticket number, name, or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filter-controls">
                    <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}>
                        {countries.map(country => (
                            <option key={country} value={country}>
                                {country === "all" ? "All Countries" : country}
                            </option>
                        ))}
                    </select>

                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                        <option value="date">Sort by Date</option>
                        <option value="name">Sort by Name</option>
                        <option value="country">Sort by Country</option>
                    </select>

                    <button onClick={exportToCSV} className="btn-export">
                        <i className="bi bi-download"></i>
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Tabla de tickets */}
            <div className="tickets-table-container">
                {filteredTickets.length > 0 ? (
                    <table className="tickets-table">
                        <thead>
                            <tr>
                                <th>Ticket #</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Country</th>
                                <th>Phone</th>
                                <th>Purchase Date</th>
                                <th>Amount</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTickets.map(ticket => (
                                <tr key={ticket.userId}>
                                    <td className="ticket-number">{ticket.ticketNumber}</td>
                                    <td>{ticket.firstName} {ticket.lastName}</td>
                                    <td>{ticket.email}</td>
                                    <td>
                                        <span className="country-badge">{ticket.country}</span>
                                    </td>
                                    <td>{ticket.phone || "N/A"}</td>
                                    <td>{new Date(ticket.purchaseDate).toLocaleDateString()}</td>
                                    <td className="amount">${ticket.ticketPrice} USDT</td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                onClick={() => viewTicketDetails(ticket)}
                                                className="btn-view"
                                                title="View Details"
                                            >
                                                <i className="bi bi-eye"></i>
                                            </button>
                                            <button
                                                onClick={() => handleCancelTicket(ticket.userId, ticket.ticketNumber)}
                                                className="btn-cancel-ticket"
                                                title="Cancel Ticket"
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="no-tickets">
                        <i className="bi bi-inbox"></i>
                        <p>No tickets found matching your criteria</p>
                    </div>
                )}
            </div>

            {/* Modal de detalles */}
            {showDetailModal && selectedTicket && (
                <div className="detail-modal-overlay" onClick={() => setShowDetailModal(false)}>
                    <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Ticket Details</h3>
                            <button onClick={() => setShowDetailModal(false)} className="close-btn">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="modal-content">
                            <div className="detail-section">
                                <h4>Ticket Information</h4>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <span className="label">Ticket Number:</span>
                                        <span className="value">{selectedTicket.ticketNumber}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">Purchase Date:</span>
                                        <span className="value">{new Date(selectedTicket.purchaseDate).toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h4>Personal Information</h4>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <span className="label">Name:</span>
                                        <span className="value">{selectedTicket.firstName} {selectedTicket.lastName}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">Email:</span>
                                        <span className="value">{selectedTicket.email}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">Phone:</span>
                                        <span className="value">{selectedTicket.phone || "Not provided"}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">Country:</span>
                                        <span className="value">{selectedTicket.country}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h4>Payment Information</h4>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <span className="label">Total Amount:</span>
                                        <span className="value highlight">${selectedTicket.ticketPrice} USDT</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">From Commission Wallet:</span>
                                        <span className="value">${selectedTicket.paidFromComision} USDT</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">From Dividend Wallet:</span>
                                        <span className="value">${selectedTicket.paidFromDividend} USDT</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">User ID:</span>
                                        <span className="value">{selectedTicket.userId}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TicketAdminPanel;
